import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ExternalLink, GraduationCap, Award, Globe, Brain, Target, Shield, Compass, Heart, Users } from "lucide-react";
import { ABOUT } from "@/constants/testIds";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const credentials = [
  { 
    icon: Globe,
    degree: "Master of Brain and Mind Sciences",
    institution: "University of Sydney, Australia"
  },
  { 
    icon: Award,
    degree: "MSc Psychology",
    institution: "Nottingham Trent University, UK"
  },
  { 
    icon: GraduationCap,
    degree: "B.Sc. Psychology & Public Health",
    institution: "Bowling Green State University, USA"
  },
];

const clinicalPillars = [
  {
    icon: Brain,
    title: "Observer-Led Methodology",
    description: "Direct clinician-administered observation in controlled environments, not retrospective parent-report questionnaires."
  },
  {
    icon: Target,
    title: "Gold-Standard Instrumentation",
    description: "ADOS-2, WISC-IV/V, BRIEF-2, Vineland-3 — internationally validated psychometric instruments."
  },
  {
    icon: Shield,
    title: "Biopsychosocial Formulation",
    description: "Comprehensive mapping of neurological substrates, psychological profiles, and environmental modulators."
  },
];

export const AboutPage = () => {
  return (
    <div data-testid={ABOUT.section}>
      <Helmet>
        <title>About Us | Modern Minds - Child Psychology Centre in Lucknow</title>
        <meta name="description" content="Meet Isha Rastogi, founder of Modern Minds. With degrees from University of Sydney, Nottingham Trent & Bowling Green, she brings world-class neurodevelopmental expertise to Lucknow families." />
        <link rel="canonical" href="https://www.modernminds.co.in/about" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            <motion.div variants={fadeInUp} className="gradient-divider mx-auto mb-6" />
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            >
              About Us
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="font-body text-base sm:text-lg text-[#E2E8F0] max-w-2xl mx-auto"
            >
              The institution and the mind that drives it.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 1: MODERN MINDS (Institutional Identity) */}
      <section className="py-8 sm:py-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <span className="inline-block bg-[#C9A962]/20 text-[#C9A962] text-xs font-body font-semibold px-3 py-1 rounded mb-4">
              THE INSTITUTION
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Modern Minds
            </h2>
            <p className="font-body text-lg sm:text-xl text-[#C9A962] italic">
              Where Science Meets Sanctuary.
            </p>
          </motion.div>

          {/* Purpose, Aim, Target Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Purpose */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="glass-card p-6"
            >
              <div className="w-12 h-12 bg-[#C9A962]/20 rounded-lg flex items-center justify-center mb-4">
                <Compass size={24} className="text-[#C9A962]" />
              </div>
              <h3 className="font-heading text-lg font-bold text-white mb-3">
                Purpose
              </h3>
              <p className="font-body text-sm text-[#E0E1DD] leading-relaxed">
                Modern Minds exists to replace the chaos of trial-and-error parenting with a high-resolution, data-driven diagnostic framework. We provide clarity where confusion once reigned, transforming uncertainty into actionable insight.
              </p>
            </motion.div>

            {/* Aim */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="glass-card p-6"
            >
              <div className="w-12 h-12 bg-[#7C9885]/20 rounded-lg flex items-center justify-center mb-4">
                <Heart size={24} className="text-[#7C9885]" />
              </div>
              <h3 className="font-heading text-lg font-bold text-white mb-3">
                Aim
              </h3>
              <p className="font-body text-sm text-[#E0E1DD] leading-relaxed">
                To integrate advanced translational neuroscience with practical, heart-centered daily living. We bridge the gap between clinical precision and the warmth of family connection.
              </p>
            </motion.div>

            {/* Target */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="glass-card p-6"
            >
              <div className="w-12 h-12 bg-[#3D9A8B]/20 rounded-lg flex items-center justify-center mb-4">
                <Users size={24} className="text-[#3D9A8B]" />
              </div>
              <h3 className="font-heading text-lg font-bold text-white mb-3">
                Target
              </h3>
              <p className="font-body text-sm text-[#E0E1DD] leading-relaxed">
                Neurodivergent children and the family systems that require precision-guided support to thrive. We serve those who seek understanding beyond labels and intervention beyond guesswork.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 2: MIND BEHIND MODERN MINDS (Founder Identity) */}
      <section data-testid={ABOUT.teamSection} className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <span className="inline-block bg-[#7C9885]/20 text-[#7C9885] text-xs font-body font-semibold px-3 py-1 rounded mb-4">
              THE FOUNDER
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              The Mind Behind Modern Minds
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Photo Column with Neural Pathway Design */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2 flex items-center justify-center py-16"
            >
              <div className="relative" style={{ width: '360px', height: '360px' }}>
                {/* Circular glow effect */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-gradient-to-br from-[#C9A962]/30 via-[#7C9885]/20 to-[#3D9A8B]/30 blur-2xl"></div>
                
                {/* Circular Photo - Center */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32">
                  <div className="relative w-full h-full rounded-full overflow-hidden border-2 border-[#C9A962]/60 shadow-xl shadow-[#C9A962]/30">
                    <img
                      src="https://customer-assets.emergentagent.com/job_modern-minds-landing/artifacts/8e2jq15i_PHOTO-2026-06-30-15-46-25.jpg"
                      alt="Isha Rastogi - Founder & Clinical Director"
                      className="w-full h-full object-cover object-top"
                      width="128"
                      height="128"
                    />
                  </div>
                </div>

                {/* SVG Neural Pathways - All 8 lines radiating from center */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 360 360" data-testid="neural-pathway">
                  {/* Center is at 180,180. Inner radius ~70px (edge of photo), outer to ~140px */}
                  
                  {/* 0° - Right */}
                  <line x1="250" y1="180" x2="320" y2="180" stroke="#C9A962" strokeWidth="2" strokeOpacity="0.7"/>
                  {/* 45° - Top Right */}
                  <line x1="230" y1="130" x2="280" y2="80" stroke="#7C9885" strokeWidth="2" strokeOpacity="0.7"/>
                  {/* 90° - Top */}
                  <line x1="180" y1="110" x2="180" y2="40" stroke="#3D9A8B" strokeWidth="2" strokeOpacity="0.7"/>
                  {/* 135° - Top Left */}
                  <line x1="130" y1="130" x2="80" y2="80" stroke="#C9A962" strokeWidth="2" strokeOpacity="0.7"/>
                  {/* 180° - Left */}
                  <line x1="110" y1="180" x2="40" y2="180" stroke="#7C9885" strokeWidth="2" strokeOpacity="0.7"/>
                  {/* 225° - Bottom Left */}
                  <line x1="130" y1="230" x2="80" y2="280" stroke="#3D9A8B" strokeWidth="2" strokeOpacity="0.7"/>
                  {/* 270° - Bottom */}
                  <line x1="180" y1="250" x2="180" y2="320" stroke="#C9A962" strokeWidth="2" strokeOpacity="0.7"/>
                  {/* 315° - Bottom Right */}
                  <line x1="230" y1="230" x2="280" y2="280" stroke="#7C9885" strokeWidth="2" strokeOpacity="0.7"/>
                </svg>

                {/* Floating Cloud Keywords */}
                {/* 90° - Top */}
                <div className="absolute left-1/2 -translate-x-1/2 top-0" style={{ top: '5px' }}>
                  <div className="px-3 py-1.5 rounded-full bg-[#3D9A8B]/20 backdrop-blur-sm border border-[#3D9A8B]/30 shadow-lg">
                    <span className="font-body text-[10px] text-[#3D9A8B] font-semibold whitespace-nowrap">ADOS-2</span>
                  </div>
                </div>
                
                {/* 45° - Top Right */}
                <div className="absolute" style={{ right: '5px', top: '50px' }}>
                  <div className="px-3 py-1.5 rounded-full bg-[#7C9885]/20 backdrop-blur-sm border border-[#7C9885]/30 shadow-lg">
                    <span className="font-body text-[10px] text-[#7C9885] font-semibold whitespace-nowrap">Data-Driven</span>
                  </div>
                </div>
                
                {/* 0° - Right */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2" style={{ right: '0px' }}>
                  <div className="px-3 py-1.5 rounded-full bg-[#C9A962]/20 backdrop-blur-sm border border-[#C9A962]/30 shadow-lg">
                    <span className="font-body text-[10px] text-[#C9A962] font-semibold whitespace-nowrap">Empathy</span>
                  </div>
                </div>
                
                {/* 315° - Bottom Right */}
                <div className="absolute" style={{ right: '5px', bottom: '50px' }}>
                  <div className="px-3 py-1.5 rounded-full bg-[#7C9885]/20 backdrop-blur-sm border border-[#7C9885]/30 shadow-lg">
                    <span className="font-body text-[10px] text-[#7C9885] font-semibold whitespace-nowrap">Biopsychosocial</span>
                  </div>
                </div>
                
                {/* 270° - Bottom */}
                <div className="absolute left-1/2 -translate-x-1/2" style={{ bottom: '5px' }}>
                  <div className="px-3 py-1.5 rounded-full bg-[#C9A962]/20 backdrop-blur-sm border border-[#C9A962]/30 shadow-lg">
                    <span className="font-body text-[10px] text-[#C9A962] font-semibold whitespace-nowrap">Healing</span>
                  </div>
                </div>
                
                {/* 225° - Bottom Left */}
                <div className="absolute" style={{ left: '5px', bottom: '50px' }}>
                  <div className="px-3 py-1.5 rounded-full bg-[#3D9A8B]/20 backdrop-blur-sm border border-[#3D9A8B]/30 shadow-lg">
                    <span className="font-body text-[10px] text-[#3D9A8B] font-semibold whitespace-nowrap">Family-First</span>
                  </div>
                </div>
                
                {/* 180° - Left */}
                <div className="absolute left-0 top-1/2 -translate-y-1/2" style={{ left: '0px' }}>
                  <div className="px-3 py-1.5 rounded-full bg-[#7C9885]/20 backdrop-blur-sm border border-[#7C9885]/30 shadow-lg">
                    <span className="font-body text-[10px] text-[#7C9885] font-semibold whitespace-nowrap">Precision</span>
                  </div>
                </div>
                
                {/* 135° - Top Left */}
                <div className="absolute" style={{ left: '5px', top: '50px' }}>
                  <div className="px-3 py-1.5 rounded-full bg-[#C9A962]/20 backdrop-blur-sm border border-[#C9A962]/30 shadow-lg">
                    <span className="font-body text-[10px] text-[#C9A962] font-semibold whitespace-nowrap">Neuroscience</span>
                  </div>
                </div>
                
                {/* Name below */}
                <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 text-center w-full">
                  <h3 className="font-heading text-xl font-bold text-white">Isha Rastogi</h3>
                  <p className="font-body text-[10px] text-[#C9A962] uppercase tracking-wider mt-1">
                    Child Psychologist & Clinical Neuro-Development Specialist
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Content Column */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-3"
            >
              <div className="gradient-divider mb-4" />
              <p className="font-body text-xs text-[#C9A962] uppercase tracking-wider mb-2">
                Founder & Clinical Director
              </p>

              {/* Founder Bio */}
              <div className="glass-card p-6 mb-6">
                <p className="font-body text-sm text-[#E0E1DD] leading-relaxed mb-4">
                  As the Founder & Clinical Director, I founded MODERN MINDS to bridge the gap between advanced translational neuroscience and practical family life. I witnessed too many families trapped in a cycle of trial-and-error, burdened by subjective assessments that failed to capture the complexity of their child&apos;s neurological needs.
                </p>
                <p className="font-body text-sm text-[#E0E1DD] leading-relaxed">
                  MODERN MINDS was created to serve as a high-resolution diagnostic gateway. We utilize the Grassroots Psychological Protocol to provide more than just a label; we provide a clinical baseline. My commitment is to shift your family from a state of reactive navigation to a state of data-driven confidence. We reconstruct your family&apos;s architecture, ensuring that every intervention is mapped to your child&apos;s unique cognitive and sensory profile, restoring the calm and connection every family deserves.
                </p>
              </div>

              {/* Credentials */}
              <div className="space-y-3">
                {credentials.map((cred) => (
                  <div
                    key={cred.degree}
                    className="flex items-start gap-3 glass-card p-3"
                  >
                    <div className="w-8 h-8 bg-[#C9A962]/20 rounded flex items-center justify-center flex-shrink-0">
                      <cred.icon size={16} className="text-[#C9A962]" />
                    </div>
                    <div>
                      <p className="font-body font-semibold text-white text-sm">{cred.degree}</p>
                      <p className="font-body text-xs text-[#778DA9]">{cred.institution}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Learn More Link */}
              <a
                href="https://www.isharastogi.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-learn-more"
                className="inline-flex items-center gap-2 btn-outline rounded px-5 py-2.5 font-body font-medium text-sm mt-6"
              >
                Full Professional Background
                <ExternalLink size={14} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Clinical Philosophy Pillars */}
      <section className="py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-6"
          >
            <div className="gradient-divider mx-auto mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">
              Clinical Philosophy
            </h2>
            <p className="font-body text-sm text-[#778DA9]">
              The Grassroots Psychological Protocol
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-4">
            {clinicalPillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="glass-card p-5"
              >
                <div className="w-10 h-10 bg-[#C9A962]/20 rounded flex items-center justify-center mb-4">
                  <pillar.icon size={20} className="text-[#C9A962]" />
                </div>
                <h3 className="font-heading text-lg font-bold text-white mb-2">
                  {pillar.title}
                </h3>
                <p className="font-body text-sm text-[#E0E1DD] leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="glass-card-gold p-8 text-center"
          >
            <div className="gradient-divider mx-auto mb-4" />
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">
              Begin Your Diagnostic Journey
            </h2>
            <p className="font-body text-sm text-[#E0E1DD] mb-6 max-w-xl mx-auto">
              Schedule a clinical consultation to discuss your family&apos;s needs and initiate the Grassroots Psychological Protocol.
            </p>
            <a
              href="https://consult.modernminds.co.in"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-cta-schedule"
              className="inline-flex items-center justify-center gap-2 btn-gold rounded px-8 py-3 font-body font-semibold"
            >
              Schedule Consultation
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
